import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* Highlights for one Bible chapter (both Bible and commentary sources) + create/update/delete. */
export function useHighlights(book, chapter) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ["highlights", book, Number(chapter)];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/highlights", { params: { book, chapter } })).data,
    enabled: !!user && !!book && !!chapter,
    staleTime: 60 * 1000,
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["highlights"] }); qc.invalidateQueries({ queryKey: ["highlight-counts"] }); };
  const onError = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const create = useMutation({
    mutationFn: async (body) => (await api.post("/highlights", body)).data,
    onSuccess: () => { invalidate(); toast.success("Highlighted. Click it to add a label or note."); },
    onError,
  });
  const update = useMutation({ mutationFn: async ({ id, ...patch }) => (await api.patch(`/highlights/${id}`, patch)).data, onSuccess: invalidate, onError });
  const remove = useMutation({ mutationFn: async (id) => api.delete(`/highlights/${id}`), onSuccess: () => { invalidate(); toast.success("Highlight removed."); }, onError });

  const forScope = useCallback((pred) => (data?.highlights || []).filter(pred), [data]);
  return { all: data?.highlights || [], forScope, create, update, remove };
}
